import { AutomationForgeElementType } from "@redotech/redo-content-builder-common/forge/marketing-automation-forge-types";
import { CheckoutAbTestMetricToOptimize } from "@redotech/redo-conversion-core-common/checkouts/checkout-ab-test-builder";
import { defaultPostPurchaseSegment } from "@redotech/redo-conversion-core-common/post-purchase/node-defaults";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import {
  CustomerSegmentOperation,
  CustomerTagOperation,
  DoNothingStep,
  SuppliesShippingConditionStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { FlowInProgress } from "@redotech/redo-flows-common/flow-in-progress";
import { DEFAULT_SHOPPER_AGENT_MESSAGE_ALWAYS_SEND } from "@redotech/redo-flows-common/shopper-agent-message-channel";
import { WaitTimeUnit } from "@redotech/redo-flows-common/steps";
import {
  StepType,
  SuppliesShippingConditionKind,
  SuppliesShippingListOperator,
  SuppliesShippingNumberOperator,
  SuppliesShippingRateMode,
} from "@redotech/redo-flows-core-schema-common/common";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import {
  getDefaultPhoneNumberFieldName,
  getDefaultSmsRecipientNameFieldName,
} from "@redotech/redo-merchant-marketing-app/automations/utils/get-default-field-names";
import { assertNever } from "@redotech/util/type";
import { ObjectId } from "bson";
import { PartialFlowStep } from "../steps";
import {
  getDefaultEmailAddressFieldName,
  getDefaultEmailRecipientNameFieldName,
} from "../steps/send-email";
import { DEFAULT_SHOPPER_AGENT_MESSAGE_CHANNEL } from "../steps/shopper-agent-message-channel-state";
import { replacedHsaEndStepId } from "./hsa-end-replacement";
import { replacedSuppliesEndStepId } from "./supplies-end-replacement";

/** Which condition each supplies palette entry drops in pre-configured. */
const SUPPLIES_SHIPPING_SEED_CONDITIONS: Partial<
  Record<AutomationForgeElementType, SuppliesShippingConditionStep["condition"]>
> = {
  [AutomationForgeElementType.SUPPLIES_SHIPPING_CART_VALUE]: {
    kind: SuppliesShippingConditionKind.CART_VALUE,
    operator: SuppliesShippingNumberOperator.GREATER_THAN_OR_EQUAL,
    amountMinorUnits: 0,
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_FIRST_TIME_CUSTOMER]: {
    kind: SuppliesShippingConditionKind.FIRST_TIME_CUSTOMER,
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_AUTOMATED_REORDER]: {
    kind: SuppliesShippingConditionKind.AUTOMATED_REORDER,
  },
  // Deliberately empty: valid() gates Save until merchants are chosen.
  [AutomationForgeElementType.SUPPLIES_SHIPPING_MERCHANT]: {
    kind: SuppliesShippingConditionKind.MERCHANT,
    operator: SuppliesShippingListOperator.ANY_OF,
    accountIds: [],
  },
  // Likewise empty until products are chosen.
  [AutomationForgeElementType.SUPPLIES_SHIPPING_CART_ITEMS]: {
    kind: SuppliesShippingConditionKind.CART_ITEMS,
    operator: SuppliesShippingListOperator.ANY_OF,
    productIds: [],
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_FAMILY_TAGS]: {
    kind: SuppliesShippingConditionKind.PRODUCT_FAMILY_TAGS,
    operator: SuppliesShippingListOperator.ANY_OF,
    tags: [],
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_DESTINATION_PROVINCE]: {
    kind: SuppliesShippingConditionKind.DESTINATION_PROVINCE,
    operator: SuppliesShippingListOperator.ANY_OF,
    provinces: [],
  },
};

const typeToStep: Record<AutomationForgeElementType, StepType> = {
  [AutomationForgeElementType.WAIT]: StepType.WAIT,
  [AutomationForgeElementType.WAIT_FOR_EVENT]: StepType.WAIT_FOR_EVENT,
  [AutomationForgeElementType.SEND_EMAIL]: StepType.SEND_EMAIL,
  [AutomationForgeElementType.SEND_SMS]: StepType.SEND_SMS,
  [AutomationForgeElementType.SEND_WEBHOOK]: StepType.SEND_WEBHOOK,
  [AutomationForgeElementType.MANAGE_CUSTOMER_TAGS]:
    StepType.MANAGE_CUSTOMER_TAGS,
  [AutomationForgeElementType.MANAGE_STATIC_SEGMENT]:
    StepType.MANAGE_STATIC_SEGMENT,
  [AutomationForgeElementType.RECOVER]: StepType.RECOVER,
  [AutomationForgeElementType.SHOPPER_AGENT_MESSAGE]:
    StepType.SHOPPER_AGENT_MESSAGE,
  [AutomationForgeElementType.BRANCH]: StepType.CONDITION,
  [AutomationForgeElementType.END]: StepType.DO_NOTHING,
  [AutomationForgeElementType.UPSELL]: StepType.UPSELL,
  [AutomationForgeElementType.DOWNSELL]: StepType.DOWNSELL,
  [AutomationForgeElementType.OMS_ACTION]: StepType.OMS_ACTION,
  [AutomationForgeElementType.OMS_CONDITION]: StepType.OMS_CONDITION,
  [AutomationForgeElementType.OMS_CODE]: StepType.OMS_CODE,
  [AutomationForgeElementType.RCO_PARCEL_CONDITION]:
    StepType.RCO_CHECKOUT_PACKAGE_CONDITION,
  [AutomationForgeElementType.RCO_PARCEL]: StepType.RCO_CHECKOUT_PACKAGE,
  [AutomationForgeElementType.CHECKOUT_EXPERIENCE]:
    StepType.CHECKOUT_EXPERIENCE,
  [AutomationForgeElementType.BLOCK_CHECKOUT]: StepType.BLOCK_CHECKOUT,
  [AutomationForgeElementType.RCO_SEGMENT]: StepType.RCO_SEGMENT,
  [AutomationForgeElementType.RCO_SPLIT]: StepType.RCO_SPLIT,
  [AutomationForgeElementType.AB_TEST]: StepType.AB_TEST,
  [AutomationForgeElementType.POST_PURCHASE_PAGE]: StepType.POST_PURCHASE_PAGE,
  [AutomationForgeElementType.POST_PURCHASE_SEGMENT]:
    StepType.POST_PURCHASE_SEGMENT,
  [AutomationForgeElementType.POST_PURCHASE_SPLIT]:
    StepType.POST_PURCHASE_SPLIT,
  [AutomationForgeElementType.TRACKING_PAGE]: StepType.TRACKING_PAGE,
  [AutomationForgeElementType.RULE_CONDITIONS]: StepType.RULE_CONDITIONS,
  [AutomationForgeElementType.RULE_ACTIONS]: StepType.RULE_ACTIONS,
  [AutomationForgeElementType.RULE_CONDITIONS_V3]: StepType.RULE_CONDITIONS_V3,
  [AutomationForgeElementType.RULE_ACTIONS_V3]: StepType.RULE_ACTIONS_V3,
  [AutomationForgeElementType.WORK_ORDER_WORK_STEP]:
    StepType.WORK_ORDER_WORK_STEP,
  [AutomationForgeElementType.WORK_ORDER_CONDITION]:
    StepType.WORK_ORDER_CONDITION,
  [AutomationForgeElementType.PROCESS_RETURN]: StepType.PROCESS_RETURN,
  [AutomationForgeElementType.CONCIERGE_BLOCK]: StepType.CONCIERGE_BLOCK,
  [AutomationForgeElementType.SUPPLIES_SHIPPING_CART_VALUE]:
    StepType.SUPPLIES_SHIPPING_CONDITION,
  [AutomationForgeElementType.SUPPLIES_SHIPPING_FIRST_TIME_CUSTOMER]:
    StepType.SUPPLIES_SHIPPING_CONDITION,
  [AutomationForgeElementType.SUPPLIES_SHIPPING_AUTOMATED_REORDER]:
    StepType.SUPPLIES_SHIPPING_CONDITION,
  [AutomationForgeElementType.SUPPLIES_SHIPPING_MERCHANT]:
    StepType.SUPPLIES_SHIPPING_CONDITION,
  [AutomationForgeElementType.SUPPLIES_SHIPPING_CART_ITEMS]:
    StepType.SUPPLIES_SHIPPING_CONDITION,
  [AutomationForgeElementType.SUPPLIES_SHIPPING_FAMILY_TAGS]:
    StepType.SUPPLIES_SHIPPING_CONDITION,
  [AutomationForgeElementType.SUPPLIES_SHIPPING_DESTINATION_PROVINCE]:
    StepType.SUPPLIES_SHIPPING_CONDITION,
  [AutomationForgeElementType.SUPPLIES_SHIPPING_RATE]:
    StepType.SUPPLIES_SHIPPING_RATE,
  [AutomationForgeElementType.HSA_QUESTION_GROUP]: StepType.HSA_QUESTION_GROUP,
  [AutomationForgeElementType.HSA_CONDITION]: StepType.HSA_CONDITION,
  [AutomationForgeElementType.HSA_KNOCKOUT]: StepType.HSA_KNOCKOUT,
  [AutomationForgeElementType.HSA_CONTENT]: StepType.HSA_CONTENT,
  [AutomationForgeElementType.HSA_END]: StepType.HSA_END,
};

// HSA node ids are branded UUIDs (hsaNodeIdSchema), not ObjectIds.
const HSA_FORGE_ELEMENT_TYPES = new Set([
  AutomationForgeElementType.HSA_QUESTION_GROUP,
  AutomationForgeElementType.HSA_CONDITION,
  AutomationForgeElementType.HSA_KNOCKOUT,
  AutomationForgeElementType.HSA_CONTENT,
]);

// The node id's first uuid segment: short, but unique within a flow.
const HSA_HANDLE_SUFFIX_LENGTH = 8;
function defaultHsaHandle(prefix: string, nodeId: string): string {
  return `${prefix}-${nodeId.slice(0, HSA_HANDLE_SUFFIX_LENGTH)}`;
}

export const dropElement = ({
  elementType,
  idFrom,
  idTo,
  conditionSide,
  edgeId,
  automation,
  schema,
  setSelected,
}: {
  elementType: AutomationForgeElementType;
  idFrom: string;
  idTo?: string;
  conditionSide?: boolean;
  edgeId?: string;
  automation: FlowInProgress;
  schema: Schema;
  setSelected: (n: string | undefined) => void;
}) => {
  if (!automation) {
    return [];
  }

  const newNode = {
    id: HSA_FORGE_ELEMENT_TYPES.has(elementType)
      ? crypto.randomUUID()
      : new ObjectId().toString(),
    type: typeToStep[elementType],
  } as PartialFlowStep;

  const newSteps = [...automation.steps, newNode];

  const fromNodeIndex = newSteps.findIndex((step) => step.id === idFrom);
  if (fromNodeIndex === -1) {
    return [];
  }

  const fromNode = newSteps[fromNodeIndex];
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

  // connect previous node to the new node

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  switch (fromNode.type) {
    case StepType.TRIGGER:
    case StepType.WAIT:
    case StepType.WAIT_FOR_EVENT:
    case StepType.RECOGNIZE_EMAIL:
    case StepType.SEND_EMAIL:
    case StepType.SEND_SMS:
    case StepType.SEND_WEBHOOK:
    case StepType.MANAGE_CUSTOMER_TAGS:
    case StepType.MANAGE_STATIC_SEGMENT:
    case StepType.RECOVER:
    case StepType.OMS_ACTION:
    case StepType.OMS_CODE:
    case StepType.UPSELL:
    case StepType.DOWNSELL:
    case StepType.TRACKING_PAGE:
    case StepType.WORK_ORDER_WORK_STEP:
    case StepType.RULE_ACTIONS:
    case StepType.RULE_ACTIONS_V3:
    case StepType.PROCESS_RETURN:
    case StepType.COMPENSATION_STEP:
    case StepType.RESTOCKING_STEP:
    case StepType.CONCIERGE_BLOCK:
    case StepType.SHOPPER_AGENT_MESSAGE:
    case StepType.HSA_QUESTION_GROUP:
    case StepType.HSA_CONTENT:
      // Add nextId directly for these types
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      fromNode.nextId = newNode.id;
      break;
    case StepType.AB_TEST:
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (fromNode.variants && fromNode.variants.length > 0) {
        // Create new node to insert before the current target
        let targetNodeId = null;

        // If edgeId is provided, find and update that specific variant
        if (edgeId) {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          const variant = fromNode.variants.find((v) => v.id === edgeId);
          if (variant) {
            // Save the original nextId to connect our new node to it
            targetNodeId = variant.nextId;
            // Connect the variant to our new node
            variant.nextId = newNode.id;
          }
        }

        if (targetNodeId) {
          // Set the idTo to the variant's nextId so that in the switch below
          // we can connect the new node to the correct target
          idTo = targetNodeId;
        }
      }
      break;
    case StepType.RCO_SPLIT:
    case StepType.POST_PURCHASE_SPLIT:
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (fromNode.treatments && fromNode.treatments.length > 0 && edgeId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const treatment = fromNode.treatments.find(
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          (t) => t.treatmentId === edgeId,
        );
        if (treatment) {
          treatment.nextId = newNode.id;
        }
      }
      break;
    case StepType.CONDITION:
    case StepType.OMS_CONDITION:
    case StepType.UPSELL_CONDITION:
    case StepType.RCO_CHECKOUT_PACKAGE_CONDITION:
    case StepType.WORK_ORDER_CONDITION:
      if (conditionSide) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fromNode.nextTrueId = newNode.id;
      } else {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fromNode.nextFalseId = newNode.id;
      }
      break;
    case StepType.RULE_CONDITIONS:
    case StepType.RULE_CONDITIONS_V3: {
      const updatedFromNode = conditionSide
        ? { ...fromNode, nextTrueId: newNode.id }
        : { ...fromNode, nextFalseId: newNode.id };
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      newSteps[fromNodeIndex] = updatedFromNode;
      break;
    }
    case StepType.RCO_SEGMENT:
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (fromNode.edges && edgeId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const edge = fromNode.edges.find((e) => e.edgeId === edgeId);
        if (edge) {
          edge.nextId = newNode.id;
        }
      } else if (conditionSide) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fromNode.matchingNextId = newNode.id;
      } else {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fromNode.nonMatchingNextId = newNode.id;
      }
      break;
    case StepType.POST_PURCHASE_SEGMENT:
    case StepType.HSA_CONDITION:
    case StepType.SUPPLIES_SHIPPING_CONDITION:
      if (conditionSide) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fromNode.matchingNextId = newNode.id;
      } else {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fromNode.nonMatchingNextId = newNode.id;
      }
      break;
    case StepType.POST_PURCHASE_PAGE:
      if (conditionSide) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fromNode.acceptNextId = newNode.id;
      } else {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        fromNode.declineNextId = newNode.id;
      }
      break;
    case StepType.RCO_CHECKOUT_PACKAGE:
    case StepType.CHECKOUT_EXPERIENCE:
    case StepType.BLOCK_CHECKOUT:
    case StepType.DO_NOTHING:
    case StepType.HSA_END:
    case StepType.HSA_KNOCKOUT:
    case StepType.SUPPLIES_SHIPPING_RATE:
      break;
    default:
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      assertNever(fromNode);
  }

  // connect the new node to the next node
  if (idTo) {
    switch (newNode.type) {
      case StepType.TRIGGER:
      case StepType.WAIT:
      case StepType.WAIT_FOR_EVENT:
      case StepType.RECOGNIZE_EMAIL:
      case StepType.SEND_EMAIL:
      case StepType.SEND_SMS:
      case StepType.SEND_WEBHOOK:
      case StepType.MANAGE_CUSTOMER_TAGS:
      case StepType.MANAGE_STATIC_SEGMENT:
      case StepType.RECOVER:
      case StepType.OMS_ACTION:
      case StepType.OMS_CODE:
      case StepType.UPSELL:
      case StepType.DOWNSELL:
      case StepType.TRACKING_PAGE:
      case StepType.WORK_ORDER_WORK_STEP:
      case StepType.RULE_ACTIONS:
      case StepType.PROCESS_RETURN:
      case StepType.COMPENSATION_STEP:
      case StepType.RESTOCKING_STEP:
      case StepType.RULE_ACTIONS_V3:
      case StepType.CONCIERGE_BLOCK:
      case StepType.SHOPPER_AGENT_MESSAGE: {
        newNode.nextId = idTo;
        break;
      }
      case StepType.AB_TEST: {
        const variantAId = new ObjectId().toString();
        const variantBId = new ObjectId().toString();

        const alternativeVariantEndNode = {
          id: new ObjectId().toString(),
          type: StepType.DO_NOTHING,
        } as PartialFlowStep;

        // Add end node to the steps
        newSteps.push(alternativeVariantEndNode);

        // First variant connects to original target, second variant gets new end node
        newNode.variants = [
          {
            id: variantAId,
            name: "Variant A",
            weight: 50,
            nextId: idTo, // Connect to original target
          },
          {
            id: variantBId,
            name: "Variant B",
            weight: 50,
            nextId: alternativeVariantEndNode.id,
          },
        ];
        break;
      }
      case StepType.CONDITION:
      case StepType.RETURN_FLOW_CONDITION:
      case StepType.OMS_CONDITION:
      case StepType.UPSELL_CONDITION:
      case StepType.RULE_CONDITIONS:
      case StepType.RULE_CONDITIONS_V3:
      case StepType.WORK_ORDER_CONDITION: {
        // When a branch node is inserted into a path
        // Connect the true branch to the original target (idTo)
        newNode.nextTrueId = idTo;

        // Create an end node for the false branch
        const falseEndNode = {
          id: new ObjectId().toString(),
          type: StepType.DO_NOTHING,
        } as PartialFlowStep;

        // Add end node to steps collection
        newSteps.push(falseEndNode);

        // Connect the branch's false path to the end node
        newNode.nextFalseId = falseEndNode.id;

        break;
      }
      case StepType.RCO_CHECKOUT_PACKAGE_CONDITION: {
        newNode.nextTrueId = idTo;
        break;
      }
      case StepType.POST_PURCHASE_SEGMENT:
      case StepType.RCO_SEGMENT: {
        if (idTo) {
          newNode.matchingNextId = idTo;
        }
        break;
      }
      case StepType.RCO_SPLIT:
      case StepType.POST_PURCHASE_SPLIT: {
        const controlTreatmentId = new ObjectId().toString();
        const variantTreatmentId = new ObjectId().toString();

        newNode.treatments = [
          {
            treatmentId: controlTreatmentId,
            name: "A: Control",
            weight: 50,
            nextId: idTo,
          },
          {
            treatmentId: variantTreatmentId,
            name: "B: Variant",
            weight: 50,
            nextId: undefined,
          },
        ];
        break;
      }
      case StepType.POST_PURCHASE_PAGE: {
        newNode.acceptNextId = idTo;
        break;
      }
      case StepType.HSA_QUESTION_GROUP:
      case StepType.HSA_CONTENT: {
        newNode.nextId = idTo;
        break;
      }
      case StepType.HSA_CONDITION: {
        // Matching branch takes the original target; the non-matching
        // branch is left open as a drop zone (no auto end node).
        newNode.matchingNextId = idTo;
        break;
      }
      case StepType.SUPPLIES_SHIPPING_CONDITION: {
        // Matching branch takes the original target; the seed below terminates
        // the non-matching branch with an End step.
        newNode.matchingNextId = idTo;
        break;
      }
      // Terminal steps: nothing downstream to relink.
      case StepType.RCO_CHECKOUT_PACKAGE:
      case StepType.CHECKOUT_EXPERIENCE:
      case StepType.BLOCK_CHECKOUT:
      case StepType.DO_NOTHING:
      case StepType.HSA_END:
      case StepType.HSA_KNOCKOUT:
      case StepType.SUPPLIES_SHIPPING_RATE:
        break;
      default:
        assertNever(newNode);
    }
  }

  // set up the new node's default fields
  switch (newNode.type) {
    case StepType.RECOGNIZE_EMAIL:
      newNode.recipientUserIds = [];
      break;
    case StepType.SEND_EMAIL:
      newNode.emailAddressFieldName = getDefaultEmailAddressFieldName(schema);
      newNode.recipientNameFieldName =
        getDefaultEmailRecipientNameFieldName(schema);
      break;
    case StepType.SEND_SMS:
      newNode.phoneNumberFieldName = getDefaultPhoneNumberFieldName(schema);
      newNode.recipientNameFieldName =
        getDefaultSmsRecipientNameFieldName(schema);
      break;
    case StepType.SEND_WEBHOOK:
      newNode.destinationUrl = "";
      newNode.headers = [];
      newNode.payload = "{}";
      newNode.disabled = false;
      break;
    case StepType.UPSELL:
      newNode.upsellId = new ObjectId().toString();
      break;
    case StepType.DOWNSELL:
      newNode.downsellId = new ObjectId().toString();
      break;
    case StepType.POST_PURCHASE_SEGMENT:
      newNode.segment = defaultPostPurchaseSegment;
      if (!newNode.matchingNextId) {
        // create a do nothing step for the matching edge
        const doNothingStep: DoNothingStep = {
          type: StepType.DO_NOTHING,
          id: new ObjectId().toString(),
        };
        newSteps.push(doNothingStep);
        newNode.matchingNextId = doNothingStep.id;
      }
      if (!newNode.nonMatchingNextId) {
        // create a do nothing step for the non-matching edge
        const doNothingStep: DoNothingStep = {
          type: StepType.DO_NOTHING,
          id: new ObjectId().toString(),
        };
        newSteps.push(doNothingStep);
        newNode.nonMatchingNextId = doNothingStep.id;
      }
      break;
    case StepType.POST_PURCHASE_PAGE: {
      if (!newNode.acceptNextId) {
        // create a do nothing step for the accept edge
        const doNothingStep: DoNothingStep = {
          type: StepType.DO_NOTHING,
          id: new ObjectId().toString(),
        };
        newSteps.push(doNothingStep);
        newNode.acceptNextId = doNothingStep.id;
      }
      if (!newNode.declineNextId) {
        // create a do nothing step for the decline edge
        const doNothingStep: DoNothingStep = {
          type: StepType.DO_NOTHING,
          id: new ObjectId().toString(),
        };
        newSteps.push(doNothingStep);
        newNode.declineNextId = doNothingStep.id;
      }
      break;
    }
    case StepType.RCO_SPLIT: {
      newNode.splitId = new ObjectId().toString();
      newNode.name = "New A/B Test";
      newNode.hypothesis = "";
      newNode.metricToOptimize =
        CheckoutAbTestMetricToOptimize.CHECKOUT_CONVERSION_RATE;
      newNode.isActive = false;
      if (!newNode.treatments) {
        newNode.treatments = [
          {
            treatmentId: new ObjectId().toString(),
            name: "A: Control",
            weight: 50,
            nextId: undefined,
          },
          {
            treatmentId: new ObjectId().toString(),
            name: "B: Variant",
            weight: 50,
            nextId: undefined,
          },
        ];
      }
      break;
    }
    case StepType.POST_PURCHASE_SPLIT: {
      newNode.splitId = new ObjectId().toString();
      newNode.name = "New A/B Test";
      newNode.isActive = false;
      // Mirror POST_PURCHASE_PAGE / POST_PURCHASE_SEGMENT: auto-create a
      // DO_NOTHING step for any treatment whose arm wasn't pre-linked, so
      // the flow editor renders an explicit End node and the saved flow
      // preserves the empty-arm edge (a legitimate "test no post-purchase
      // page" treatment).
      if (newNode.treatments) {
        for (const treatment of newNode.treatments) {
          if (!treatment.nextId) {
            const doNothingStep: DoNothingStep = {
              type: StepType.DO_NOTHING,
              id: new ObjectId().toString(),
            };
            newSteps.push(doNothingStep);
            treatment.nextId = doNothingStep.id;
          }
        }
      }
      break;
    }
    case StepType.WAIT:
      newNode.numDays = 1;
      newNode.timeUnit = WaitTimeUnit.DAYS;
      break;
    case StepType.AB_TEST:
    case StepType.CONDITION:
    case StepType.RETURN_FLOW_CONDITION:
    case StepType.WAIT_FOR_EVENT:
    case StepType.UPSELL_CONDITION:
    case StepType.TRIGGER:
    case StepType.OMS_ACTION:
    case StepType.OMS_CONDITION:
    case StepType.OMS_CODE:
    case StepType.WORK_ORDER_CONDITION:
    case StepType.RCO_CHECKOUT_PACKAGE_CONDITION:
    case StepType.RCO_CHECKOUT_PACKAGE:
    case StepType.CHECKOUT_EXPERIENCE:
    case StepType.BLOCK_CHECKOUT:
    case StepType.RCO_SEGMENT:
    case StepType.DO_NOTHING:
    case StepType.HSA_END:
    case StepType.TRACKING_PAGE:
    case StepType.PROCESS_RETURN:
    case StepType.RESTOCKING_STEP:
      break;
    case StepType.HSA_QUESTION_GROUP:
      // Deliberately invalid until filled in so valid() gates Save.
      newNode.groupHandle = defaultHsaHandle("group", newNode.id);
      newNode.questions = [];
      break;
    case StepType.HSA_CONDITION:
      newNode.condition = {
        conjunction: ConjunctionMode.AND,
        conditionBlocks: [{ conjunction: ConjunctionMode.AND, conditions: [] }],
      };
      break;
    case StepType.HSA_KNOCKOUT:
      newNode.reason = "";
      break;
    // Seeded from the palette entry dragged, so the kind is already chosen.
    case StepType.SUPPLIES_SHIPPING_CONDITION:
      newNode.condition = SUPPLIES_SHIPPING_SEED_CONDITIONS[elementType];
      // Both branches terminate, so no configuration leaves a dangling edge.
      if (!newNode.matchingNextId) {
        const matchingEnd: DoNothingStep = {
          type: StepType.DO_NOTHING,
          id: new ObjectId().toString(),
        };
        newSteps.push(matchingEnd);
        newNode.matchingNextId = matchingEnd.id;
      }
      if (!newNode.nonMatchingNextId) {
        const nonMatchingEnd: DoNothingStep = {
          type: StepType.DO_NOTHING,
          id: new ObjectId().toString(),
        };
        newSteps.push(nonMatchingEnd);
        newNode.nonMatchingNextId = nonMatchingEnd.id;
      }
      break;
    case StepType.SUPPLIES_SHIPPING_RATE:
      newNode.rate = {
        mode: SuppliesShippingRateMode.FIXED,
        amountMinorUnits: 0,
      };
      break;
    case StepType.HSA_CONTENT:
      newNode.contentHandle = defaultHsaHandle("content", newNode.id);
      newNode.heading = "";
      newNode.body = [""];
      newNode.recordsConsent = false;
      break;
    case StepType.COMPENSATION_STEP:
      newNode.compensationMethods = [];
      newNode.notifyCustomer = true;
      break;
    case StepType.RULE_CONDITIONS:
    case StepType.RULE_CONDITIONS_V3:
      newNode.conditionGroups = { operator: "AND", conditions: [] };
      break;
    case StepType.RULE_ACTIONS:
    case StepType.RULE_ACTIONS_V3:
      newNode.actions = [];
      break;
    case StepType.WORK_ORDER_WORK_STEP:
      newNode.title = "";
      newNode.displayAttributes = [];
      newNode.inputs = [];
      newNode.outputs = [];
      newNode.stationId = null;
      break;
    case StepType.MANAGE_CUSTOMER_TAGS:
      // Set default values for the new unified customer tag management step
      newNode.operation = CustomerTagOperation.ADD;
      newNode.tagIds = [];
      newNode.disabled = false;
      break;
    case StepType.MANAGE_STATIC_SEGMENT:
      newNode.operation = CustomerSegmentOperation.ADD;
      newNode.segmentId = undefined;
      newNode.disabled = false;
      break;
    case StepType.RECOVER:
      newNode.disabled = false;
      newNode.phoneNumberFieldName = getDefaultPhoneNumberFieldName(schema);
      break;
    case StepType.CONCIERGE_BLOCK:
      newNode.prompt = "";
      newNode.disabled = false;
      break;
    case StepType.SHOPPER_AGENT_MESSAGE:
      newNode.channel = { ...DEFAULT_SHOPPER_AGENT_MESSAGE_CHANNEL };
      newNode.alwaysSend = DEFAULT_SHOPPER_AGENT_MESSAGE_ALWAYS_SEND;
      newNode.disabled = false;
      break;
    default:
      assertNever(newNode);
  }

  setSelected(newNode.id);

  const replacedEndStepId =
    replacedHsaEndStepId(newSteps, newNode.type, idTo) ??
    replacedSuppliesEndStepId(newSteps, newNode.type, idTo);
  if (replacedEndStepId !== undefined) {
    return newSteps.filter((step) => step.id !== replacedEndStepId);
  }

  return newSteps;
};
